'use client';

import { useEffect, useRef, useState } from 'react';
import { KARROT_SEARCH_URL, type Review } from './reviews-data';

const NAVY = '#1A3A5C';
const NAVY_DEEP = '#0F2640';
const NAVY_SOFT = '#E5EEF6';
const NAVY_TINT = '#F0F5FA';
const INK = '#0F1F33';
const INK_SOFT = '#4A5C73';
const INK_MUTE = '#7E8FA3';
const STAR_GOLD = '#F2B019';
const KR_CARD = '#0E0E0E';
const KR_TEXT_MUTE = '#8B8B8B';
const KR_BADGE_BG = '#1B2A3D';
const KR_BADGE_ICON = '#4A90E2';
const KR_LINE = 'rgba(255,255,255,0.08)';
const CARROT = '#FF6F0F';
const CARROT_DEEP = '#D9580A';

export default function ReviewsSection({ reviews }: { reviews: Review[] }) {
  if (!reviews || reviews.length === 0) return null;
  return (
    <section
      id="reviews"
      className="relative overflow-hidden"
      style={{ background: '#ffffff' }}
    >
      <div className="relative z-10 max-w-[1000px] mx-auto px-6 py-20 sm:py-32">
        <ReviewsHero />
        <div className="h-12 sm:h-16" />
        <ReviewsSlider reviews={reviews} />
        <div className="h-12 sm:h-16" />
        <ReviewsCTA />
      </div>
    </section>
  );
}

function ReviewsHero() {
  return (
    <div>
      {/* Eyebrow */}
      <p
        className="inline-flex items-center gap-2.5 text-s font-extrabold tracking-[0.22em] uppercase mb-3"
        style={{ color: INK_MUTE }}
      >
        모두가 만족하는, 당나귀배송
      </p>

      {/* Headline */}
      <h2
        className="font-black leading-[1.15] mb-2.5"
        style={{ fontSize: 'clamp(2rem, 4vw, 2.5rem)', color: NAVY }}
      >
        실제 이용 고객들의 경험
      </h2>

      {/* Subhead */}
      <p
        className="text-base font-medium leading-[1.7] mb-12 max-w-[520px]"
        style={{ color: INK_SOFT }}
      >
        편리하고, 저렴하고, 안심하고, 깔끔한 서비스 경험.
        <br />
        직접 이용해본 고객들은 더 이상 운반으로 고민할 필요가 없습니다.
      </p>

      {/* Stats box */}
      <div
        className="relative overflow-hidden rounded-[28px]"
        style={{
          background: `linear-gradient(135deg, ${NAVY_TINT} 0%, ${NAVY_SOFT} 100%)`,
          padding: 28,
        }}
      >
        {/* navy glow */}
        <span
          aria-hidden
          className="pointer-events-none absolute"
          style={{
            top: -120,
            right: -120,
            width: 320,
            height: 320,
            background: `radial-gradient(circle, ${NAVY}33 0%, transparent 65%)`,
          }}
        />

        <div className="relative grid items-center gap-6 sm:gap-8 grid-cols-1 md:grid-cols-[auto_auto_1fr]">
          {/* Star */}
          <div
            aria-hidden
            className="text-center md:text-left leading-none"
            style={{
              color: STAR_GOLD,
              fontSize: 'clamp(72px, 12vw, 140px)',
              textShadow: '0 4px 12px rgba(242,176,25,0.25)',
            }}
          >
            ★
          </div>

          {/* 4.9 */}
          <div className="text-center md:text-left">
            <div className="flex items-end justify-center md:justify-start gap-2">
              <span
                className="font-black leading-none"
                style={{
                  fontSize: 'clamp(72px, 10vw, 144px)',
                  color: NAVY,
                  letterSpacing: '-0.06em',
                }}
              >
                4.9
              </span>
              <span
                className="font-bold leading-none pb-2"
                style={{
                  fontSize: 'clamp(26px, 3.5vw, 50px)',
                  color: INK_MUTE,
                  letterSpacing: '-0.03em',
                }}
              >
                / 5.0
              </span>
            </div>
            <p
              className="mt-3 text-center md:text-left font-bold"
              style={{ color: NAVY, fontSize: 17 }}
            >
              평균 만족도
            </p>
          </div>

          {/* Right helper */}
          <div
            className="md:pl-7 md:border-l md:border-t-0 border-t pt-6 md:pt-0"
            style={{ borderColor: 'rgba(15,31,51,0.10)' }}
          >
            <p
              className="text-[14.5px] leading-[1.7] mb-4"
              style={{ color: INK_SOFT }}
            >
              당근 동네 주민들이 직접 남긴
              <br />
              {/* <strong style={{ color: INK }}>1,000건 이상</strong>의 */}
              실 이용 후기 기준입니다.
            </p>
            <span
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[12.5px] font-extrabold"
              style={{
                background: '#ffffff',
                border: `1.5px solid ${CARROT}`,
                color: CARROT,
              }}
            >
              <span
                aria-hidden
                className="inline-block w-1.5 h-1.5 rounded-full"
                style={{ background: CARROT }}
              />
              Karrot Verified
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReviewsSlider({ reviews }: { reviews: Review[] }) {
  const sliderRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLSpanElement>(null);

  const cardWidth = () => {
    const el = sliderRef.current;
    if (!el) return 320;
    const card = el.querySelector('[data-card]') as HTMLElement | null;
    const gap = 18;
    return (card?.offsetWidth ?? 320) + gap;
  };

  const scrollByCard = (dir: 'prev' | 'next') => {
    const el = sliderRef.current;
    if (!el) return;
    const dx = (dir === 'next' ? 1 : -1) * cardWidth();
    el.scrollBy({ left: dx, behavior: 'smooth' });
  };

  useEffect(() => {
    const el = sliderRef.current;
    const bar = barRef.current;
    if (!el || !bar) return;

    const onScroll = () => {
      const max = el.scrollWidth - el.clientWidth;
      const pct = max > 0 ? (el.scrollLeft / max) * 100 : 0;
      bar.style.width = `${Math.max(18, 18 + pct * 0.6)}%`;
    };
    onScroll();
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      scrollByCard('next');
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      scrollByCard('prev');
    }
  };

  return (
    <div>
      {/* Slider header */}
      <div className="flex items-end justify-between gap-4 mb-5">
        <div>
          <h3
            className="font-extrabold leading-tight"
            style={{ color: INK, fontSize: 22, letterSpacing: '-0.01em' }}
          >
            고객님들의 생생한 후기
          </h3>
          <p className="mt-1 text-[13px]" style={{ color: INK_MUTE }}>
            당근 동네 주민 후기를 그대로 가져왔습니다
          </p>
        </div>
        <div className="flex gap-2.5">
          <NavButton dir="prev" onClick={() => scrollByCard('prev')} />
          <NavButton dir="next" onClick={() => scrollByCard('next')} />
        </div>
      </div>

      {/* Slider rail */}
      <div
        ref={sliderRef}
        role="region"
        aria-label="고객 후기 슬라이더"
        tabIndex={0}
        onKeyDown={onKeyDown}
        className="reviews-rail flex gap-[18px] overflow-x-auto outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        style={{
          scrollSnapType: 'x mandatory',
          scrollBehavior: 'smooth',
          scrollbarWidth: 'none',
          marginLeft: -24,
          marginRight: -24,
          paddingLeft: 24,
          paddingRight: 24,
        }}
      >
        {reviews.map((r, i) => (
          <ReviewCard key={r.id} review={r} priority={i === 0} />
        ))}
      </div>

      {/* Progress rail */}
      <div
        className="mx-auto mt-7"
        style={{ width: 200, height: 4, background: 'rgba(15,31,51,0.08)', borderRadius: 999 }}
        aria-hidden
      >
        <span
          ref={barRef}
          className="block h-full rounded-full"
          style={{ width: '18%', background: NAVY, transition: 'width .25s ease' }}
        />
      </div>

      <style jsx>{`
        .reviews-rail::-webkit-scrollbar {
          display: none;
        }
        @media (prefers-reduced-motion: reduce) {
          .reviews-rail {
            scroll-behavior: auto !important;
          }
        }
      `}</style>
    </div>
  );
}

function NavButton({ dir, onClick }: { dir: 'prev' | 'next'; onClick: () => void }) {
  const [hover, setHover] = useState(false);
  const isPrev = dir === 'prev';
  return (
    <button
      type="button"
      aria-label={isPrev ? '이전 후기' : '다음 후기'}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="flex items-center justify-center transition-all"
      style={{
        width: 46,
        height: 46,
        borderRadius: 999,
        background: hover ? NAVY : '#ffffff',
        color: hover ? '#ffffff' : NAVY,
        border: `1px solid ${hover ? NAVY : 'rgba(15,31,51,0.12)'}`,
        boxShadow: hover ? '0 8px 22px rgba(15,31,51,0.18)' : '0 2px 6px rgba(15,31,51,0.06)',
        transform: hover ? 'translateY(-2px)' : 'none',
      }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
        {isPrev ? (
          <path
            d="M15 18l-6-6 6-6"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : (
          <path
            d="M9 6l6 6-6 6"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
      </svg>
    </button>
  );
}

function ReviewCard({ review, priority }: { review: Review; priority?: boolean }) {
  const [imgOk, setImgOk] = useState<boolean>(Boolean(review.imageUrl));
  const hasImage = Boolean(review.imageUrl) && imgOk;
  const [hover, setHover] = useState(false);

  return (
    <article
      data-card
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="flex-shrink-0 overflow-hidden transition-all"
      style={{
        flex: '0 0 280px',
        scrollSnapAlign: 'start',
        background: KR_CARD,
        color: '#ffffff',
        borderRadius: 18,
        border: `1px solid ${KR_LINE}`,
        boxShadow: hover
          ? '0 18px 40px rgba(0,0,0,0.35)'
          : '0 6px 18px rgba(0,0,0,0.18)',
        transform: hover ? 'translateY(-4px)' : 'none',
        width: 280,
      }}
    >
      {hasImage && review.imageUrl && (
        <div
          className="relative w-full"
          style={{ height: 160, background: '#1a1a1a' }}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={review.imageUrl}
            alt={review.imageAlt ?? ''}
            loading={priority ? 'eager' : 'lazy'}
            onError={() => setImgOk(false)}
            className="w-full h-full object-cover"
          />
          <span
            aria-hidden
            className="absolute inset-x-0 bottom-0"
            style={{
              height: 60,
              background: 'linear-gradient(to top, rgba(0,0,0,0.55), transparent)',
            }}
          />
        </div>
      )}

      <div className="p-5">
        {/* Resident badge */}
        <span
          className="inline-flex items-center gap-1.5 mb-3"
          style={{
            background: KR_BADGE_BG,
            padding: '6px 11px 6px 8px',
            borderRadius: 6,
            fontSize: 12.5,
            fontWeight: 600,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden>
            <circle cx="7" cy="7" r="7" fill={KR_BADGE_ICON} />
            <path
              d="M3.8 7.2l2.2 2.1 4.2-4.4"
              stroke="#ffffff"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
          동네 주민 후기
        </span>

        {/* Rating */}
        <div className="flex items-center gap-1.5 mb-2.5" style={{ fontSize: 13 }}>
          <span aria-hidden style={{ color: CARROT, fontSize: 15 }}>
            ★
          </span>
          <span className="font-bold">{review.rating}</span>
          <span aria-hidden style={{ color: KR_TEXT_MUTE }}>
            ·
          </span>
          <span style={{ color: KR_TEXT_MUTE }}>{review.timeAgo}</span>
          <span className="sr-only">5점 만점에 {review.rating}점</span>
        </div>

        {/* Body */}
        <p
          className="text-[14.5px] leading-[1.62]"
          style={{
            whiteSpace: 'pre-line',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: hasImage ? 5 : 7,
            overflow: 'hidden',
          }}
        >
          {review.text}
        </p>
      </div>
    </article>
  );
}

function ReviewsCTA() {
  const [hover, setHover] = useState(false);
  return (
    <div
      className="relative overflow-hidden"
      style={{
        background: NAVY,
        borderRadius: 24,
        padding: '28px',
        color: '#ffffff',
      }}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute"
        style={{
          top: -100,
          right: -80,
          width: 280,
          height: 280,
          background: 'radial-gradient(circle, rgba(255,255,255,0.10) 0%, transparent 65%)',
        }}
      />

      <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6 md:gap-10 sm:p-3">
        <div>
          <h3
            className="font-extrabold mb-2"
            style={{ fontSize: 'clamp(20px, 2.5vw, 24px)', color: '#ffffff', letterSpacing: '-0.01em' }}
          >
            더 많은 후기가 궁금하세요?
          </h3>
          <p
            className="text-[15px] leading-[1.65]"
            style={{ color: 'rgba(255,255,255,0.78)' }}
          >
            당근에서 <strong style={{ color: '#fff' }}>당나귀배송</strong>을 검색하면
            <br className="hidden sm:block" />
            수많은 실 이용고객의 솔직한 후기를 모두 확인하실 수 있어요.
          </p>
        </div>

        <a
          href={KARROT_SEARCH_URL}
          target="_blank"
          rel="noopener noreferrer"
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          className="inline-flex items-center gap-3 self-start md:self-auto whitespace-nowrap transition-all"
          style={{
            background: hover ? CARROT_DEEP : CARROT,
            color: '#ffffff',
            padding: '16px 22px',
            borderRadius: 14,
            fontWeight: 700,
            fontSize: 15,
            boxShadow: hover
              ? '0 14px 30px rgba(255,111,15,0.45)'
              : '0 6px 16px rgba(255,111,15,0.30)',
            transform: hover ? 'translateY(-2px)' : 'none',
          }}
        >
          <span
            aria-hidden
            className="inline-flex items-center justify-center"
            style={{
              width: 26,
              height: 26,
              background: '#ffffff',
              borderRadius: 999,
              fontSize: 14,
              lineHeight: 1,
            }}
          >
            🥕
          </span>
          당근에서 후기 더 보기
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M5 12h14M13 6l6 6-6 6"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </a>
      </div>
    </div>
  );
}
